/* eslint-disable no-console */
/**
 * AI Call Script Service
 * Handles 3 parallel AI API calls for call script analysis:
 * 1. Lead Classification - status, intent, readiness score, budget, availability
 * 2. AI Summary - summary text, key quote, concerns, auto tags
 * 3. Recommended Actions - book appointment, emails, whatsapp, reminders
 */

import { callApi } from 'src/utils/apiCall';
import { store } from 'src/redux/store';

/**
 * Helper to extract AI response content from API response
 * API returns: { ok: true, status: 200, data: { content: "...", model: "...", ... } }
 */
const extractAIContent = (response) => {
  // Log for debugging
  console.log('[AI Service] Raw response:', response);

  // The callApi returns { ok, status, data, headers }
  // where data is the actual API response body
  const data = response?.data;

  if (!data) {
    console.error('[AI Service] No data in response');
    return null;
  }

  // The AI API returns { content: "...", model: "...", finish_reason: "...", usage: {...} }
  // The content field contains the JSON string we need
  const content = data?.content  // Direct content field
    || data?.choices?.[0]?.message?.content  // OpenAI format fallback
    || data?.message?.content  // Alternative format
    || data?.response;  // Another fallback

  console.log('[AI Service] Extracted content:', content);

  if (!content) {
    console.error('[AI Service] No content found in response data:', data);
    return null;
  }

  return typeof content === 'string' ? content : JSON.stringify(content);
};

/**
 * Helper to safely parse JSON from AI response
 */
const parseJSONResponse = (content) => {
  if (!content) {
    console.error('[AI Service] Cannot parse null/undefined content');
    return null;
  }

  try {
    // Try to extract JSON from markdown code blocks first
    const jsonMatch = content.match(/```(?:json)?\s*([\s\S]*?)```/);
    if (jsonMatch) {
      console.log('[AI Service] Found JSON in code block');
      return JSON.parse(jsonMatch[1].trim());
    }
    // Extract JSON object block if AI returned prose + JSON
    const objectMatch = content.match(/\{[\s\S]*\}/);
    if (objectMatch) {
      try {
        console.log('[AI Service] Found JSON object in content');
        return JSON.parse(objectMatch[0]);
      } catch (e) {
        // fall through to direct parse
      }
    }
    // Try direct JSON parse
    const parsed = JSON.parse(content);
    console.log('[AI Service] Parsed JSON successfully:', parsed);
    return parsed;
  } catch (e) {
    console.error('[AI Service] Failed to parse AI JSON response:', e, 'Content:', content);
    return null;
  }
};

/**
 * Fetch recent WhatsApp messages for a lead (for AI context)
 * Tries to get the last 5 messages from either Redux state or API
 */
export const fetchWhatsAppMessagesForContext = async (practiceId, leadInfo) => {
  try {
    // First, check if we have messages in whatsappV2 Redux state
    const state = store.getState();
    const whatsappState = state?.whatsappV2;
    const existingMessages = whatsappState?.whatsappChats;
    const chatMetaData = whatsappState?.whatsAppChatMetaData;

    // If we have messages for the same lead, use them
    if (existingMessages?.length > 0 && chatMetaData?.leadId === leadInfo?.id) {
      console.log('[AI Service] Using existing WhatsApp messages from Redux state');
      return existingMessages.slice(0, 5);
    }

    // Otherwise, try to fetch messages via API
    const phoneNumber = leadInfo?.lead_contact?.phone;
    if (!phoneNumber || !practiceId) {
      console.log('[AI Service] No phone number or practiceId, skipping WhatsApp messages fetch');
      return [];
    }

    // Get current WhatsApp business account and phone from state
    const currentBusinessId = whatsappState?.currentBusinessId;
    const currentWhatsappPhoneId = whatsappState?.currentWhatsappPhoneId;

    if (!currentBusinessId || currentBusinessId === -1 || !currentWhatsappPhoneId || currentWhatsappPhoneId === -1) {
      console.log('[AI Service] No WhatsApp business account configured, skipping messages fetch');
      return [];
    }

    // Use exact lead FK filter to find thread (prevents wrong-thread mismatch)
    let thread = null;
    if (leadInfo?.id) {
      console.log('[AI Service] Searching for WhatsApp thread by lead ID:', leadInfo.id);
      const leadThreadResponse = await callApi(
        `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/?lead=${leadInfo.id}&is_archived_included=true&ordering=-created_at`,
        'GET'
      );
      thread = leadThreadResponse?.data?.results?.[0];
    }

    // Fallback: phone search only for orphan threads (no lead linked)
    if (!thread?.id) {
      const cleanPhone = phoneNumber.trim().replace(/\s+/g, '').replace(/\+/g, '');
      console.log('[AI Service] Fallback: searching for WhatsApp thread by phone:', cleanPhone);
      const phoneThreadResponse = await callApi(
        `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/search/?query=${cleanPhone}&is_archived_included=true&offset=0&ordering=-created_at`,
        'GET'
      );
      const fallbackThread = phoneThreadResponse?.data?.results?.[0];
      if (fallbackThread && !fallbackThread.lead?.id) {
        thread = fallbackThread;
      }
    }
    if (!thread?.id) {
      console.log('[AI Service] No WhatsApp thread found for lead');
      return [];
    }

    // Fetch messages for this thread
    console.log('[AI Service] Fetching WhatsApp messages for thread:', thread.id);
    const messagesResponse = await callApi(
      `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${thread.id}/whatsapp-messages/?ordering=-created_time&limit=5`,
      'GET'
    );

    const messages = messagesResponse?.data?.results || [];
    console.log('[AI Service] Fetched', messages.length, 'WhatsApp messages for context');
    // API returns messages newest-first (ordering=-created_time), but we need oldest-first for context
    return messages.reverse();
  } catch (error) {
    console.error('[AI Service] Error fetching WhatsApp messages:', error);
    return [];
  }
};

/**
 * Build context string from call script data
 * @param {Object} formData - Form data from call script
 * @param {Object} cardNotes - Notes for each card
 * @param {string} callNotes - Additional call notes
 * @param {Array} cards - Call script cards configuration
 * @param {Object} leadInfo - Lead information
 * @param {Object} aiConfig - AI config with practiceInfo, treatmentInfo, pricingInfo
 * @param {Array} whatsappMessages - Recent WhatsApp messages for context (last 5)
 */
const buildCallScriptContext = (formData, cardNotes, callNotes, cards, leadInfo, aiConfig, whatsappMessages = []) => {
  // Build Q&A pairs from form data and cards
  const qaItems = [];

  cards?.forEach((card, idx) => {
    const cardTitle = card?.title || `Question ${idx + 1}`;
    const cardElements = card?.elements || [];

    cardElements.forEach((element) => {
      if (element.field && formData[element.field] !== undefined && formData[element.field] !== '') {
        const label = element.label || element.field;
        let value = formData[element.field];

        // Handle arrays (multi-select)
        if (Array.isArray(value)) {
          value = value.join(', ');
        }

        qaItems.push(`Q: ${label}\nA: ${value}`);
      }
    });

    // Add card notes if present
    if (cardNotes?.[idx]) {
      qaItems.push(`[Note for "${cardTitle}"]: ${cardNotes[idx]}`);
    }
  });

  // Build lead info summary
  const leadSummary = leadInfo ? `
LEAD INFORMATION:
- Name: ${leadInfo.first_name || ''} ${leadInfo.last_name || ''}
- Phone: ${leadInfo.lead_contact?.phone || 'Not provided'}
- Email: ${leadInfo.lead_contact?.email || 'Not provided'}
- Preferred Contact: ${leadInfo.lead_contact?.preferred_contact_method || 'Not specified'}
- Treatment(s): ${leadInfo.lead_treatments?.map(t => t.treatment?.name || t.treatment_name || t.treatment).join(', ') || 'Not specified'}
- Current Status: ${leadInfo.lead_treatments?.[0]?.status || 'NEW'}
- Source: ${leadInfo.lead_source?.type || 'Not specified'}
` : '';

  // Build practice info from aiConfig if available
  const practiceInfo = aiConfig?.practiceInfo || '';
  const treatmentInfo = aiConfig?.treatmentInfo || '';
  const pricingInfo = aiConfig?.pricingInfo || '';

  const practiceSummary = (practiceInfo || treatmentInfo || pricingInfo) ? `
PRACTICE INFORMATION:
${practiceInfo}

${treatmentInfo ? `TREATMENT INFORMATION:\n${treatmentInfo}` : ''}

${pricingInfo ? `PRICING INFORMATION:\n${pricingInfo}` : ''}
` : '';

  // Build WhatsApp message history for context (last 5 messages)
  const whatsappContext = whatsappMessages && whatsappMessages.length > 0 ? `
RECENT WHATSAPP MESSAGES (for context):
${whatsappMessages.slice(-5).map(msg => {
    const sender = msg.direction === 'outgoing' || msg.is_outbound ? 'Practice' : 'Patient';
    const timestamp = msg.timestamp ? new Date(msg.timestamp).toLocaleString() : '';
    const content = msg.text || msg.body || msg.message || '';
    return `[${timestamp}] ${sender}: ${content}`;
  }).join('\n')}
` : '';

  return `
${practiceSummary}
${leadSummary}
${whatsappContext}
CALL SCRIPT RESPONSES:
${qaItems.join('\n\n')}

${callNotes ? `ADDITIONAL CALL NOTES:\n${callNotes}` : ''}
`.trim();
};

/**
 * 1. Lead Classification AI Call
 * Returns: classification, readinessScore, budget, availability, intent
 */
export const callLeadClassificationAI = async (practiceId, context) => {
  const systemPrompt = `You are an expert dental practice lead analyst. Analyze the call script data and classify the lead.

Your task is to evaluate:
1. Lead Classification (Hot Lead, Warm Lead, Cold Lead, or Not Interested)
2. Readiness Score (0-100) - How ready is the patient to proceed with treatment
3. Budget Assessment - What are their budget preferences/constraints
4. Availability - When can they come in for appointments
5. Intent Level - High, Medium, or Low

Consider these factors:
- Urgency indicators (timeline, events, pain points)
- Budget discussions (finance interest, price sensitivity)
- Engagement level (questions asked, enthusiasm)
- Objections or concerns raised
- Previous dental history and experience

RESPOND ONLY WITH VALID JSON in this exact format:
{
  "classification": "Hot Lead | Warm Lead | Cold Lead | Not Interested",
  "classificationEmoji": "🔥 | 🌡️ | ❄️ | ⛔",
  "readinessScore": 0-100,
  "budget": "string describing budget preference",
  "availability": "string describing availability",
  "intent": "High | Medium | Low",
  "intentIndicators": ["array", "of", "key", "indicators"]
}`;

  const messages = [
    { role: 'user', content: context }
  ];

  try {
    console.log('[AI Service] Calling Lead Classification API...');
    const response = await callApi(
      `/api/v1/practices/${practiceId}/ai/chat/`,
      'POST',
      { system_prompt: systemPrompt, messages },
      false,
      { timeout: 60000 }
    );

    console.log('[AI Service] Lead Classification response:', response?.ok, response?.status);

    // Check if request was successful
    if (response?.ok || response?.status === 200 || response?.status === 201) {
      const content = extractAIContent(response);
      const parsed = parseJSONResponse(content);

      if (parsed) {
        console.log('[AI Service] Lead Classification parsed successfully');
        return {
          success: true,
          data: {
            classification: parsed.classification || 'Warm Lead',
            classificationEmoji: parsed.classificationEmoji || '🌡️',
            readinessScore: parsed.readinessScore || 50,
            budget: parsed.budget || 'Not discussed',
            availability: parsed.availability || 'To be confirmed',
            intent: parsed.intent || 'Medium',
            intentIndicators: parsed.intentIndicators || []
          }
        };
      }
      console.error('[AI Service] Lead Classification: Failed to parse response');
    } else {
      console.error('[AI Service] Lead Classification: Request failed', response);
    }

    return { success: false, error: 'Failed to parse classification response' };
  } catch (error) {
    console.error('[AI Service] Lead Classification AI Error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * 2. AI Summary Call
 * Returns: aiSummaryText, keyQuote, concerns, tags, sentiment
 */
export const callAISummaryGeneration = async (practiceId, context) => {
  const systemPrompt = `You are an expert dental practice call analyst. Generate a comprehensive summary of the call.

Your task is to provide:
1. AI Summary - A professional 2-3 sentence summary of the call highlighting key points
2. Key Quote - The most significant quote from the patient (something they said that reveals their motivation/concerns)
3. Patient Concerns - List of concerns the patient raised with descriptions
4. Auto Tags - Categorized tags for CRM organization
5. Sentiment - Overall call sentiment

For Auto Tags, use these categories:
- treatment: Treatment types discussed (e.g., "Dental Implants", "Invisalign", "Veneers")
- source: How they found the practice (e.g., "Google Search", "Referral", "Facebook")
- payment: Payment preferences (e.g., "Finance", "Cash", "Insurance")
- urgency: Time sensitivity (e.g., "Urgent", "Time-sensitive", "Flexible")

RESPOND ONLY WITH VALID JSON in this exact format:
{
  "aiSummaryText": "Professional summary of the call...",
  "keyQuote": "Direct quote from patient...",
  "concerns": [
    {
      "title": "Concern title",
      "description": "Detailed description of the concern"
    }
  ],
  "tags": [
    { "label": "Tag Name", "type": "treatment|source|payment|urgency" }
  ],
  "sentiment": "Positive | Neutral | Negative",
  "highlights": ["Key point 1", "Key point 2", "Key point 3"]
}`;

  const messages = [
    { role: 'user', content: context }
  ];

  try {
    console.log('[AI Service] Calling AI Summary API...');
    const response = await callApi(
      `/api/v1/practices/${practiceId}/ai/chat/`,
      'POST',
      { system_prompt: systemPrompt, messages },
      false,
      { timeout: 60000 }
    );

    console.log('[AI Service] AI Summary response:', response?.ok, response?.status);

    // Check if request was successful
    if (response?.ok || response?.status === 200 || response?.status === 201) {
      const content = extractAIContent(response);
      const parsed = parseJSONResponse(content);

      if (parsed) {
        console.log('[AI Service] AI Summary parsed successfully');
        return {
          success: true,
          data: {
            aiSummaryText: parsed.aiSummaryText || 'Summary not available',
            keyQuote: parsed.keyQuote || '',
            concerns: parsed.concerns || [],
            tags: parsed.tags || [],
            sentiment: parsed.sentiment || 'Neutral',
            highlights: parsed.highlights || []
          }
        };
      }
      console.error('[AI Service] AI Summary: Failed to parse response');
    } else {
      console.error('[AI Service] AI Summary: Request failed', response);
    }

    return { success: false, error: 'Failed to parse summary response' };
  } catch (error) {
    console.error('[AI Service] AI Summary Generation Error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * 3. Recommended Actions AI Call
 * Returns: nextSteps with actions for booking, emails, whatsapp, reminders
 */
export const callRecommendedActionsAI = async (practiceId, context, options) => {
  const {
    emailTemplates = [],
    whatsappTemplates = [],
    appointmentMethods = [],
    appointmentTypes = [],
    practiceUsers = [],
    treatments = [],
    pricingInfo = ''
  } = options;

  // Build template lists for AI context
  const emailTemplateNames = emailTemplates.map(t => t.name || t.subject).filter(Boolean);
  const whatsappTemplateNames = whatsappTemplates.map(t => t.name).filter(Boolean);
  const methodOptions = appointmentMethods.map(m => m.label || m.value).join(', ');
  const typeOptions = appointmentTypes.map(t => t.label || t.value).join(', ');
  const userOptions = practiceUsers.map(u => `${u.first_name} ${u.last_name} (${u.role || 'Staff'})`).join(', ');
  const treatmentOptions = treatments.map(t => t.treatment?.name || t.treatment_name || t.name).filter(Boolean).join(', ');

  // Get current date for AI context
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0]; // YYYY-MM-DD
  const todayFormatted = today.toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const systemPrompt = `You are an expert dental practice CRM assistant. Based on the call analysis, recommend the best next actions.

CURRENT DATE: ${todayFormatted} (${todayStr})
IMPORTANT: All dates you recommend MUST be in the future (after ${todayStr}). Never recommend past dates.

AVAILABLE OPTIONS:

Email Templates: ${emailTemplateNames.length > 0 ? emailTemplateNames.join(', ') : 'None available'}

WhatsApp Templates: ${whatsappTemplateNames.length > 0 ? whatsappTemplateNames.join(', ') : 'None available'}

Appointment Methods: ${methodOptions || 'IN_PERSON, ONLINE, PHONE'}

Appointment Types: ${typeOptions || 'CONSULTATION, TREATMENT, FOLLOW_UP'}

Staff Members (Practitioners): ${userOptions || 'Not specified'}

Treatments: ${treatmentOptions || 'General consultation'}

${pricingInfo ? `PRICING INFORMATION (use this to set accurate consultation fees and treatment costs):
${pricingInfo}` : ''}

IMPORTANT: Pay attention to any practitioner preference mentioned by the patient during the call. If the patient mentioned wanting to see a specific dentist/practitioner by name, include that in the bookedWith field.
IMPORTANT: Use the pricing information above to accurately set the consultationFee field based on the treatment type being discussed.

Your task is to recommend:
1. Primary Action - Usually booking an appointment for hot/warm leads
2. Secondary Actions - Follow-up communications (email/WhatsApp)
3. Reminders - If follow-up is needed

Payment Option Guidelines (for bookAppointment.paymentOption):
- "No": Default for initial consultations or low-value treatments
- "DEPOSIT": Recommend for high-value treatments (£1000+) or committed patients
- "CONSULTATION": For paid consultations (typically £50-200)
- "TREATMENT": When patient is ready to pay full treatment cost upfront

Payment Method Guidelines (for bookAppointment.paymentMethod):
- "PAYMENT_LINK": Best for remote payments, send via WhatsApp/Email/SMS
- "PHONE": When patient wants to pay immediately over the call
- "IN_PRACTICE": When patient will pay at the appointment
- "EMAIL": For invoicing

For each action, specify:
- Whether it should be done immediately or scheduled
- Which template to use (if applicable)
- Pre-filled values based on the conversation

RESPOND ONLY WITH VALID JSON in this exact format:
{
  "nextSteps": [
    {
      "action": "Book Appointment | Send Email | Send WhatsApp | Add Reminder",
      "title": "Short action title",
      "description": "Why this action is recommended",
      "priority": "high | medium | low",
      "icon": "mdi:calendar-check | mdi:email-fast | mdi:whatsapp | mdi:bell-ring"
    }
  ],
  "bookAppointment": {
    "recommended": true/false,
    "appointmentType": "CONSULTATION | TREATMENT | FOLLOW_UP",
    "appointmentMethod": "IN_PRACTICE | VIDEO_CALL | PHONE",
    "contactMethod": "PHONE | EMAIL | WHATSAPP | SMS | VISITED",
    "suggestedDate": "YYYY-MM-DD or null if not discussed",
    "suggestedTime": "HH:MM or null if not discussed",
    "bookedWith": "Staff member name if patient mentioned a preference, otherwise null",
    "bookedWithReason": "Why this practitioner (patient preference, specialty match, or random if no preference)",
    "treatment": "Treatment name",
    "paymentOption": "No | DEPOSIT | CONSULTATION | TREATMENT - recommend based on treatment value and patient readiness",
    "paymentMethod": "PAYMENT_LINK | PHONE | IN_PRACTICE | EMAIL - how to collect the payment",
    "consultationFee": "Amount in GBP if consultation fee applies (e.g., 50, 100)",
    "notes": "Pre-consultation notes based on call"
  },
  "sendEmail": {
    "recommended": true/false,
    "templateName": "Template name from available list or null",
    "customSubject": "Suggested email subject if no template",
    "reason": "Why this email should be sent"
  },
  "sendWhatsApp": {
    "recommended": true/false,
    "templateName": "Template name from available list or null",
    "customMessage": "Suggested message if no template",
    "reason": "Why this message should be sent"
  },
  "addReminder": {
    "recommended": true/false,
    "reminderDate": "YYYY-MM-DD (MUST be after today - typically 1-7 days in the future for follow-ups)",
    "reminderTime": "HH:MM (business hours: 09:00-17:00)",
    "reminderNote": "What to remind about",
    "priority": "high | medium | low"
  }
}

REMINDER DATE GUIDELINES:
- Today is ${todayStr}. All reminder dates MUST be AFTER this date.
- For hot leads: 1-2 days from today
- For warm leads: 3-5 days from today
- For cold leads: 7-14 days from today
- Never use dates from 2024 or earlier - we are now in ${today.getFullYear()}`;

  const messages = [
    { role: 'user', content: context }
  ];

  try {
    console.log('[AI Service] Calling Recommended Actions API...');
    const response = await callApi(
      `/api/v1/practices/${practiceId}/ai/chat/`,
      'POST',
      { system_prompt: systemPrompt, messages },
      false,
      { timeout: 60000 }
    );

    console.log('[AI Service] Recommended Actions response:', response?.ok, response?.status);

    // Check if request was successful
    if (response?.ok || response?.status === 200 || response?.status === 201) {
      const content = extractAIContent(response);
      const parsed = parseJSONResponse(content);

      if (parsed) {
        console.log('[AI Service] Recommended Actions parsed successfully');
        return {
          success: true,
          data: {
            nextSteps: parsed.nextSteps || [],
            bookAppointment: parsed.bookAppointment || { recommended: false },
            sendEmail: parsed.sendEmail || { recommended: false },
            sendWhatsApp: parsed.sendWhatsApp || { recommended: false },
            addReminder: parsed.addReminder || { recommended: false }
          }
        };
      }
      console.error('[AI Service] Recommended Actions: Failed to parse response');
    } else {
      console.error('[AI Service] Recommended Actions: Request failed', response);
    }

    return { success: false, error: 'Failed to parse actions response' };
  } catch (error) {
    console.error('[AI Service] Recommended Actions AI Error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Main function to execute all 3 AI calls in parallel
 */
export const generateAICallSummary = async ({
  practiceId,
  formData,
  cardNotes,
  callNotes,
  cards,
  leadInfo,
  aiConfig,  // AI config with practiceInfo, treatmentInfo, pricingInfo
  emailTemplates,
  whatsappTemplates,
  appointmentMethods,
  appointmentTypes,
  practiceUsers,
  treatments,
  whatsappMessages = []  // Recent WhatsApp messages for AI context
}) => {
  console.log('[AI Service] Starting generateAICallSummary...');
  console.log('[AI Service] practiceId:', practiceId);
  console.log('[AI Service] leadInfo:', leadInfo?.first_name, leadInfo?.last_name);
  console.log('[AI Service] whatsappMessages provided:', whatsappMessages?.length || 0);

  // Fetch WhatsApp messages for context if not provided
  let messagesForContext = whatsappMessages || [];
  if (messagesForContext.length === 0 && leadInfo) {
    console.log('[AI Service] Fetching WhatsApp messages for AI context...');
    messagesForContext = await fetchWhatsAppMessagesForContext(practiceId, leadInfo);
    console.log('[AI Service] Fetched', messagesForContext.length, 'WhatsApp messages for context');
  }

  // Build the context string once, including WhatsApp messages for context
  const context = buildCallScriptContext(formData, cardNotes, callNotes, cards, leadInfo, aiConfig, messagesForContext);
  console.log('[AI Service] Built context, length:', context.length);

  console.log('[AI Service] Starting 3 parallel AI API calls...');

  // Execute all 3 AI calls in parallel
  const [classificationResult, summaryResult, actionsResult] = await Promise.all([
    callLeadClassificationAI(practiceId, context),
    callAISummaryGeneration(practiceId, context),
    callRecommendedActionsAI(practiceId, context, {
      emailTemplates,
      whatsappTemplates,
      appointmentMethods,
      appointmentTypes,
      practiceUsers,
      treatments,
      pricingInfo: aiConfig?.pricingInfo || ''
    })
  ]);

  console.log('[AI Service] All 3 calls completed');
  console.log('[AI Service] Classification result:', classificationResult.success, classificationResult.error);
  console.log('[AI Service] Summary result:', summaryResult.success, summaryResult.error);
  console.log('[AI Service] Actions result:', actionsResult.success, actionsResult.error);

  // Combine results
  const combinedData = {
    // From Lead Classification - keep emoji separate for proper display
    classification: classificationResult.success ? classificationResult.data.classification : 'Unable to classify',
    classificationEmoji: classificationResult.success ? classificationResult.data.classificationEmoji : '❓',
    readinessScore: classificationResult.success ? classificationResult.data.readinessScore : 50,
    budget: classificationResult.success ? classificationResult.data.budget : 'Not discussed',
    availability: classificationResult.success ? classificationResult.data.availability : 'To be confirmed',
    intent: classificationResult.success ? classificationResult.data.intent : 'Medium',
    intentIndicators: classificationResult.success ? classificationResult.data.intentIndicators : [],

    // From AI Summary
    aiSummaryText: summaryResult.success ? summaryResult.data.aiSummaryText : 'Summary generation failed',
    keyQuote: summaryResult.success ? summaryResult.data.keyQuote : '',
    concerns: summaryResult.success ? summaryResult.data.concerns : [],
    tags: summaryResult.success ? summaryResult.data.tags : [],
    sentiment: summaryResult.success ? summaryResult.data.sentiment : 'Neutral',
    highlights: summaryResult.success ? summaryResult.data.highlights : [],

    // From Recommended Actions
    nextSteps: actionsResult.success ? actionsResult.data.nextSteps : [],
    bookAppointment: actionsResult.success ? actionsResult.data.bookAppointment : { recommended: false },
    sendEmail: actionsResult.success ? actionsResult.data.sendEmail : { recommended: false },
    sendWhatsApp: actionsResult.success ? actionsResult.data.sendWhatsApp : { recommended: false },
    addReminder: actionsResult.success ? actionsResult.data.addReminder : { recommended: false },

    // Meta
    errors: {
      classification: classificationResult.success ? null : classificationResult.error,
      summary: summaryResult.success ? null : summaryResult.error,
      actions: actionsResult.success ? null : actionsResult.error
    },
    hasErrors: !classificationResult.success || !summaryResult.success || !actionsResult.success
  };

  return combinedData;
};

export default generateAICallSummary;
